import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CITIES } from "@/lib/categories";
import { AdCard } from "@/components/AdCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, MapPin, Search, BadgeCheck, Loader2, Heart, Pencil } from "lucide-react";
import { useFavorites } from "@/hooks/useFavorites";

const LIMIT = 12;

export default function Ustalar() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  const { isFav, toggle } = useFavorites();
  const sentinel = useRef(null);

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); }, []);

  const fetchPage = useCallback(async (pg) => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (city) p.set("city", city);
    if (category) p.set("category", category);
    p.set("page", pg); p.set("limit", LIMIT);
    const { data } = await api.get(`/ustalar?${p.toString()}`);
    setItems((prev) => (pg === 1 ? data.items : [...prev, ...data.items]));
    setPages(data.pages); setTotal(data.total); setPage(pg);
    setLoading(false);
  }, [q, city, category]);

  useEffect(() => { setItems([]); fetchPage(1); }, [fetchPage]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && page < pages) fetchPage(page + 1);
    }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loading, page, pages, fetchPage]);

  const withAds = [];
  items.forEach((u, i) => {
    withAds.push(<UstaCard key={u.user_id} u={u} navigate={navigate} isFav={isFav} toggle={toggle} />);
    if ((i + 1) % 6 === 0) withAds.push(<AdCard key={`ad-${i}`} compact />);
  });

  return (
    <div>
      <div className="tinted border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900">Ustalar</h1>
          <p className="text-slate-600 mt-2">Uzmanlık, konum ve puana göre doğru ustayı bulun.</p>
          <div className="bg-white rounded-2xl shadow-md border border-emerald-100 p-2 mt-5 flex flex-col sm:flex-row gap-2 max-w-4xl">
            <div className="flex-1 flex items-center gap-2 px-3">
              <Search className="h-5 w-5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Usta adı veya uzmanlık ara..." className="border-0 focus-visible:ring-0 shadow-none px-0" data-testid="ustalar-search-input" />
            </div>
            <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
              <SelectTrigger className="sm:w-48 rounded-xl" data-testid="ustalar-cat-filter"><SelectValue placeholder="Kategori" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tüm Kategoriler</SelectItem>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={city || "all"} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
              <SelectTrigger className="sm:w-40 rounded-xl" data-testid="ustalar-city-filter"><SelectValue placeholder="Şehir" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tüm Türkiye</SelectItem>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-sm text-slate-600 mb-5"><span className="font-bold text-emerald-600">{total}</span> usta bulundu</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {withAds}
        </div>
        <div ref={sentinel} className="h-10" />
        {loading && <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>}
        {!loading && items.length === 0 && <div className="bg-white rounded-2xl border border-slate-200/80 p-12 text-center text-slate-500">Uygun usta bulunamadı.</div>}
      </div>
    </div>
  );
}

function UstaCard({ u, navigate, isFav, toggle }) {
  const { user } = useAuth();
  const admin = user?.role === "admin";
  return (
    <div onClick={() => navigate(`/usta/${u.user_id}`)} data-testid={`usta-card-${u.user_id}`}
      className={`bg-white rounded-2xl overflow-hidden hover-lift cursor-pointer ${u.is_boosted ? "ring-2 ring-emerald-400 shadow-lg shadow-emerald-500/40" : "border border-slate-200/80"}`}>
      <div className="h-36 bg-emerald-50 relative flex items-center justify-center">
        {u.is_boosted && <span className="absolute top-2 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1 text-[10px] font-bold text-white brand-gradient px-2.5 py-0.5 rounded-full">★ Öne Çıkan</span>}
        {admin && <button onClick={(e) => { e.stopPropagation(); navigate(`/usta/${u.user_id}`); }} title="Yönetici düzenle" data-testid={`usta-admin-edit-${u.user_id}`} className="absolute bottom-2 right-2 z-10 h-8 w-8 rounded-full bg-violet-600 text-white flex items-center justify-center hover:bg-violet-700"><Pencil className="h-4 w-4" /></button>}
        {u.avatar ? <img src={u.avatar} alt={u.name} className="w-full h-full object-cover" /> : <span className="text-3xl font-extrabold text-emerald-300">{(u.name || "U")[0]}</span>}
        <button data-testid={`usta-fav-${u.user_id}`} onClick={(e) => { e.stopPropagation(); toggle("usta", u.user_id); }}
          className="absolute top-2 left-2 h-8 w-8 rounded-full bg-white/90 flex items-center justify-center hover:bg-white">
          <Heart className={`h-4 w-4 ${isFav("usta", u.user_id) ? "fill-emerald-500 text-emerald-500" : "text-slate-400"}`} />
        </button>
        {u.phone_verified && <span className="absolute top-2 right-2 bg-white rounded-full p-1 text-emerald-500"><BadgeCheck className="h-4 w-4" /></span>}
      </div>
      <div className="p-4">
        <div className="font-bold text-slate-900">{u.name}</div>
        <div className="text-xs text-slate-500">{u.specialty}</div>
        <div className="flex items-center gap-1 mt-2 text-xs">
          <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
          <span className="font-bold">{u.rating}</span><span className="text-slate-400">({u.review_count})</span>
          <span className="text-slate-300 mx-1">•</span>
          <span className="text-slate-500">{u.completed_jobs} iş</span>
        </div>
        <div className="flex items-center gap-1 mt-1 text-xs text-slate-400"><MapPin className="h-3 w-3" /> {(u.service_areas || [])[0]}</div>
      </div>
    </div>
  );
}
